// RFC-0944 — load/save orchestration for the user-scoped rules modal.
//
// Lifted out of the old UserRulesPage so both the modal (landing + dashboard)
// share one implementation. Key differences vs. the page:
//   - Fetches on EVERY closed→open transition (rules can change in another tab
//     or via the Director), not once-forever.
//   - Guards against stale responses with a monotonic sequence so a slow prior
//     request can't overwrite newer state after a rapid reopen.
//   - Refreshes the history/revert list after each save/revert (the PUT
//     response omits history, so the current page left a stale revert list).

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  getUserRules,
  putUserRules,
  type RulesDocumentV1,
  type RulesHistoryEntry,
  type RulesPutBody,
} from '../api/rules'

export interface UseUserRulesResult {
  doc: RulesDocumentV1 | null
  version: number
  history: RulesHistoryEntry[]
  loading: boolean
  loadError: string | null
  reload: () => void
  save(
    body: RulesPutBody,
  ): Promise<{ new_version: number; document: RulesDocumentV1 } | void>
}

export function useUserRules(isOpen: boolean): UseUserRulesResult {
  const [doc, setDoc] = useState<RulesDocumentV1 | null>(null)
  const [version, setVersion] = useState<number>(0)
  const [history, setHistory] = useState<RulesHistoryEntry[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Monotonic sequence: only the most recent load may commit its result. A
  // late-arriving response from a superseded request is dropped.
  const loadSeqRef = useRef(0)
  // Separate monotonic sequence gating EVERY `history` write (full loads AND
  // standalone history refreshes) so two quick saves can't let an older
  // refresh overwrite a newer revert list.
  const historySeqRef = useRef(0)
  const wasOpenRef = useRef(false)

  const load = useCallback(() => {
    const seq = ++loadSeqRef.current
    const hseq = ++historySeqRef.current
    setLoading(true)
    setLoadError(null)
    getUserRules({ history: true })
      .then((resp) => {
        if (seq !== loadSeqRef.current) return
        setDoc(resp.user ?? null)
        setVersion(resp.user_current_version ?? 0)
        if (hseq === historySeqRef.current) setHistory(resp.history ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (seq !== loadSeqRef.current) return
        setLoadError(err instanceof Error ? err.message : String(err))
        setLoading(false)
      })
  }, [])

  // Fetch on every closed→open transition; never cache-forever.
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      load()
    }
    wasOpenRef.current = isOpen
  }, [isOpen, load])

  // Best-effort refresh of just the history list after a successful write. The
  // PUT response carries the new doc/version but not history, and a stale
  // revert list would offer reverts that no longer make sense. Gated by
  // `historySeqRef` (bumped per refresh AND per load) so a slower refresh —
  // e.g. from an earlier of two quick saves — can't overwrite a newer list.
  const refreshHistory = useCallback(() => {
    const hseq = ++historySeqRef.current
    getUserRules({ history: true })
      .then((resp) => {
        if (hseq !== historySeqRef.current) return
        setHistory(resp.history ?? [])
      })
      .catch(() => {
        /* the write already succeeded; a failed history refresh is non-fatal */
      })
  }, [])

  const save = useCallback(
    async (
      body: RulesPutBody,
    ): Promise<{ new_version: number; document: RulesDocumentV1 } | void> => {
      const resp = await putUserRules(body)
      // A write always returns the persisted user document; a null here is an
      // invariant violation (unlike load()'s GET, where null = "no rules yet").
      // Fail loudly rather than silently resetting the editor to empty.
      if (resp.user === null) {
        throw new Error('putUserRules returned a null user document after a write')
      }
      setDoc(resp.user)
      setVersion(resp.user_current_version)
      refreshHistory()
      return { new_version: resp.user_current_version, document: resp.user }
    },
    [refreshHistory],
  )

  return { doc, version, history, loading, loadError, reload: load, save }
}
